'use client';

import React, { useEffect, useRef, useState } from 'react';

interface Props {
  primaryColor: string;
  width?: string | number;
  height?: string | number;
}

export default function IntegrationSvg({ 
  primaryColor, 
  width = "100%", 
  height = 300 
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Use Intersection Observer to trigger animation when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio > 0.3) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (svgRef.current) {
      observer.observe(svgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @keyframes flowLine {
          0% {
            stroke-dashoffset: 14;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }

        @keyframes flowLineReverse {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: 14;
          }
        }

        @keyframes pulseGlow {
          0%, 100% {
            opacity: 0.3;
            filter: blur(0px);
          }
          50% {
            opacity: 0.8;
            filter: blur(2px);
          }
        }

        .connection-line {
          stroke-dasharray: 10, 4;
          stroke-linecap: round;
          stroke-dashoffset: 0;
        }

        .connection-line-animated {
          animation: flowLine 0.8s linear infinite;
        }

        .connection-line-animated-reverse {
          animation: flowLineReverse 0.8s linear infinite;
        }

        .connection-line-glow {
          stroke-dasharray: 10, 4;
          stroke-linecap: round;
          animation: pulseGlow 2s ease-in-out infinite;
        }
      `}</style>
      <svg 
        ref={svgRef}
        width={width} 
        height={height} 
        viewBox="0 0 500 300" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Bidirectional arrows - smaller arrowheads */}
          <marker
            id="arrowRight2"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill={primaryColor} />
          </marker>
          <marker
            id="arrowLeft2"
            markerWidth="8"
            markerHeight="6"
            refX="0"
            refY="3"
            orient="auto"
          >
            <polygon points="8 0, 0 3, 8 6" fill={primaryColor} />
          </marker>
          
          {/* Filter for glow effect */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Voley in center - perfectly centered */}
        <circle cx="250" cy="150" r="42" fill={primaryColor} opacity="0.9" />
        <text x="250" y="158" textAnchor="middle" fill="white" fontSize="15" fontWeight="bold">VOLEY</text>
        
        {/* Systems on left - perfectly aligned equally around center, matching Client Communication pattern */}
        {/* AMS Box - centered at y=100 (50px above Voley center at 150), matching Client Communication */}
        <rect x="40" y="75" width="100" height="50" rx="8" fill="#f5f5f5" stroke="#d0d0d0" strokeWidth="2" />
        <text x="90" y="107" textAnchor="middle" fill="#666" fontSize="16" fontWeight="bold">AMS</text>
        
        {/* CRM Box - centered at y=200 (50px below Voley center at 150), matching Client Communication */}
        <rect x="40" y="175" width="100" height="50" rx="8" fill="#f5f5f5" stroke="#d0d0d0" strokeWidth="2" />
        <text x="90" y="207" textAnchor="middle" fill="#666" fontSize="16" fontWeight="bold">CRM</text>
        
        {/* Clients on right - better vertical spacing */}
        <g>
          <circle cx="410" cy="80" r="28" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1.5" />
          <circle cx="410" cy="68" r="11" fill="#dddddd" />
          <rect x="393" y="87" width="34" height="17" rx="8.5" fill="#dddddd" />
        </g>
        
        <g>
          <circle cx="410" cy="150" r="28" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1.5" />
          <circle cx="410" cy="138" r="11" fill="#dddddd" />
          <rect x="393" y="157" width="34" height="17" rx="8.5" fill="#dddddd" />
        </g>
        
        <g>
          <circle cx="410" cy="220" r="28" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="1.5" />
          <circle cx="410" cy="208" r="11" fill="#dddddd" />
          <rect x="393" y="227" width="34" height="17" rx="8.5" fill="#dddddd" />
        </g>

        {/* Connection lines - glow layer (behind) */}
        {isVisible && (
          <g className="connection-line-glow" filter="url(#glow)">
            <line x1="140" y1="100" x2="208" y2="135" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
            <line x1="140" y1="200" x2="208" y2="165" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
            <line x1="292" y1="135" x2="382" y2="80" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
            <line x1="292" y1="150" x2="382" y2="150" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
            <line x1="292" y1="165" x2="382" y2="220" stroke={primaryColor} strokeWidth="4" strokeOpacity="0.3" />
          </g>
        )}

        {/* Bidirectional arrows - animated dashes with proper connection points */}
        {/* Line 1: AMS to Voley - connects from AMS center-right (y=100) to Voley left, matching Client Communication pattern */}
        <line 
          x1="140" y1="100" x2="208" y2="135" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated' : ''}`}
          markerEnd="url(#arrowRight2)" 
          markerStart="url(#arrowLeft2)"
          style={{ animationDelay: '0s' }}
        />
        
        {/* Line 2: CRM to Voley - connects from CRM center-right (y=200) to Voley left, matching Client Communication pattern */}
        <line 
          x1="140" y1="200" x2="208" y2="165" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated' : ''}`}
          markerEnd="url(#arrowRight2)" 
          markerStart="url(#arrowLeft2)"
          style={{ animationDelay: '0.2s' }}
        />
        
        {/* Line 3: Voley to Client 1 (top) */}
        <line 
          x1="292" y1="135" x2="382" y2="80" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated-reverse' : ''}`}
          markerEnd="url(#arrowRight2)" 
          markerStart="url(#arrowLeft2)"
          style={{ animationDelay: '0.1s' }}
        />
        
        {/* Line 4: Voley to Client 2 (middle) */}
        <line 
          x1="292" y1="150" x2="382" y2="150" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated-reverse' : ''}`}
          markerEnd="url(#arrowRight2)" 
          markerStart="url(#arrowLeft2)"
          style={{ animationDelay: '0.3s' }}
        />
        
        {/* Line 5: Voley to Client 3 (bottom) */}
        <line 
          x1="292" y1="165" x2="382" y2="220" 
          stroke={primaryColor} 
          strokeWidth="2.5"
          className={`connection-line ${isVisible ? 'connection-line-animated-reverse' : ''}`}
          markerEnd="url(#arrowRight2)" 
          markerStart="url(#arrowLeft2)"
          style={{ animationDelay: '0.4s' }}
        />

      </svg>
    </>
  );
}
